/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import Point from '@arcgis/core/geometry/Point';
import {lngLatToXY} from '@arcgis/core/geometry/support/webMercatorUtils';
import isNil from 'lodash.isnil';

interface AssetLocation {
  longitude?: number;
  latitude?: number;
}

// the slow, general path - a WGS84 point tested by the extent itself, which
// projects it when the spatial references differ ...
const containsAssetPoint = (extent: __esri.Extent, asset: AssetLocation): boolean => (
  !!extent?.contains(new Point({
    x                : asset.longitude,
    y                : asset.latitude,
    spatialReference : {wkid: 4326}
  }))
);

// the extent's own inclusive bounds test, on plain numbers ...
const isWithinBounds = (extent: __esri.Extent, x: number, y: number): boolean => (
  x >= extent.xmin && x <= extent.xmax && y >= extent.ymin && y <= extent.ymax
);

/**
 * Returns a test for whether an asset's position lies inside an extent,
 * giving the same answer as `extent.contains(new Point({x: longitude, y:
 * latitude, spatialReference: {wkid: 4326}}))`. For a Web Mercator or WGS84
 * extent the asset is projected with the same `lngLatToXY` math the extent
 * applies internally and compared as numbers, so no `Point` is created per
 * asset; any other extent, or an asset without coordinates, takes the
 * original `Point` path.
 *
 * ```typescript
 *
 *   import {createAssetExtentTest} from './assetExtentUtils';
 *   ...
 *
 *   const isInExtent = createAssetExtentTest(view.extent.clone().expand(4));
 *   const visible    = assets.filter(isInExtent);
 *
 * ```
 *
 * @param   extent The extent to test against; `null` matches no asset.
 * @returns        A function returning `true` when the asset is inside the
 *                 extent, otherwise `false`.
 */
export const createAssetExtentTest = (extent: __esri.Extent | null | undefined) => {
  const spatialReference = extent?.spatialReference;
  const isWebMercator    = !!spatialReference?.isWebMercator;
  const isWGS84          = !!spatialReference?.isWGS84;

  return function(asset: AssetLocation): boolean {
    let isInExtent = false;

    if (asset && extent) {
      const {longitude, latitude} = asset;
      const hasLocation           = !isNil(longitude) && !isNil(latitude);

      if (hasLocation && isWebMercator) {
        const [x, y] = lngLatToXY(longitude, latitude);
        isInExtent   = isWithinBounds(extent, x, y);
      } else if (hasLocation && isWGS84) {
        isInExtent = isWithinBounds(extent, longitude, latitude);
      } else {
        isInExtent = containsAssetPoint(extent, asset);
      }
    }

    return isInExtent;
  };
};

export default createAssetExtentTest;
